import { useState, useEffect, useCallback, useRef } from "react";
import { MOCK } from "./MockData";
import { fmt } from "./Utils/Utilities";
import { RevenueChart, DonutChart, Badge } from "./SharedUI";

// ── Cylinders per site — Sky Hill 40%, Hill Side 20%, Sasol 30% ──────────────
const CYLINDERS_PER_SITE = [
  { label: "Sky Hill",  name: "Sky Hill",  value: 50 },
  { label: "Hill Side", name: "Hill Side", value: 20 },
  { label: "Sasol",     name: "Sasol",     value: 30 },
];

// ── Collections per site — cylinder type & qty collected per site ─────────────
const COLLECTIONS_PER_SITE = [
  {
    site: "Sky Hill",
    cylinders: [
      { type: "Oxygen 22.5kg",    qty: 276 },
      { type: "Acetylene 48kg", qty: 58 },
      { type: "Nitrogen 22.5kg",  qty: 742 },
    ],
  },
  {
    site: "Hill Side",
    cylinders: [
      { type: "Oxygen 22.5kg",    qty: 294 },
      { type: "CO₂ 22.5kg",       qty: 4 },
      { type: "Argon 22.5kg",     qty: 118 },
    ],
  },
  {
    site: "Sasol",
    cylinders: [
      { type: "Oxygen 22.5kg",    qty: 442 },
      { type: "Nitrogen 22.5kg",  qty: 8  },
      { type: "Hydrogen 22.5kg",  qty: 14  },
      { type: "Acetylene 48kg", qty: 44  },
    ],
  },
];

// ── Inventory — real cylinder types & site names ─────────────────────────────
const INVENTORY = [
  { id: 1, cylinder: "Oxygen 22.5kg",    stock: 320, maxStock: 400, status: "OK",       site: "Sky Hill"  },
  { id: 2, cylinder: "Acetylene 48kg",   stock: 48,  maxStock: 200, status: "Critical", site: "Sky Hill"  },
  { id: 3, cylinder: "Nitrogen 22.5kg",  stock: 130, maxStock: 300, status: "Low",      site: "Hill Side" },
  { id: 4, cylinder: "Argon 22.5kg",     stock: 210, maxStock: 250, status: "OK",       site: "Hill Side" },
  { id: 5, cylinder: "CO₂ 22.5kg",       stock: 75,  maxStock: 200, status: "Low",      site: "Hill Side" },
  { id: 6, cylinder: "Oxygen 22.5kg",    stock: 580, maxStock: 600, status: "OK",       site: "Sasol"     },
  { id: 7, cylinder: "Hydrogen 22.5kg",  stock: 40,  maxStock: 150, status: "Critical", site: "Sasol"     },
  { id: 8, cylinder: "Nitrogen 22.5kg",  stock: 290, maxStock: 350, status: "OK",       site: "Sasol"     },
];

function DashboardPage() {
  const d = MOCK.kpis;

  // Total cylinders — fixed display value; donut uses relative % values
  const totalCylinders = 2000;

  const kpis = [
    {
      label: "Total Expenses",
      value: fmt.currency(d.totalRevenue),
      growth: d.revenueGrowth,
      icon: <i className="bi bi-wallet2" />,
      color: "c-flame",
      foot: "YTD 2026",
    },
    {
      label: "Active Orders",
      value: fmt.number(d.activeOrders),
      growth: d.ordersGrowth,
      icon: <i className="bi bi-box-seam" />,
      color: "c-blue",
      foot: `${d.pendingDeliveries} pending delivery`,
    },
    {
      label: "Collection Rate",
      value: fmt.pct(d.collectionRate),
      growth: d.collectionGrowth,
      icon: <i className="bi bi-wallet2" />,
      color: "c-green",
      foot: `${d.overdueCollections} overdue accounts`,
    },
    {
      label: "Total Cylinders",
      value: fmt.number(totalCylinders),
      growth: d.customerGrowth,
      icon: <i className="bi bi-people-fill" />,
      color: "c-amber",
      foot: "Total cylinders across all sites",
    },
  ];

  // Site names for Recent Orders (cycling through the 3 sites)
  const sites = ["Sky Hill", "Hill Side", "Sasol"];

  return (
    <div className="page">
      {/* KPI Row */}
      <div className="kpi-grid" style={{ alignItems: "stretch" }}>
        {kpis.map((k) => (
          <div
            key={k.label}
            className={`kpi ${k.color}`}
            style={{ display: "flex", flexDirection: "column", minHeight: 160 }}
          >
            <div className="kpi-row">
              <span className="kpi-ico">{k.icon}</span>
              <span className={`kpi-trend ${k.growth >= 0 ? "up" : "dn"}`}>
                {k.growth >= 0 ? "▲" : "▼"} {Math.abs(k.growth)}%
              </span>
            </div>
            <div className="kpi-val">{k.value}</div>
            <div className="kpi-lbl">{k.label}</div>
            <div style={{ marginTop: "auto", paddingTop: 8, display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: "var(--text3)" }}>
              <i className="bi bi-pin" /> {k.foot}
            </div>
          </div>
        ))}
      </div>

      {/* Donut Charts Row — Orders by Product + Cylinders per Site */}
      <div className="g2" style={{ gap: '10px', marginTop: '10px',  }}>
        <div className="panel">
          <div className="ph">
            <span className="ph-title">Orders by Product</span>
          </div>
          <div className="pb">
            <DonutChart data={MOCK.ordersByProduct} size={110} />
          </div>
        </div>

        <div className="panel">
          <div className="ph">
            <span className="ph-title">Cylinders per Site</span>
          </div>
          <div className="pb" style={{ display: "flex", alignItems: "center", gap: 28, height: 150}}>
            {/* Custom SVG donut — guaranteed site name labels */}
            {(() => {
              const slices = [
                { name: "Sky Hill",  pct: 50, color: "#4F8EF7" },
                { name: "Hill Side", pct: 20, color: "#FF6B3D" },
                { name: "Sasol",     pct: 30, color: "#F5C842" },
              ];
              const r = 54, cx = 65, cy = 65, stroke = 22;
              const circ = 2 * Math.PI * r;
              let offset = 0;
              const segments = slices.map((s) => {
                const dash = (s.pct / 100) * circ;
                const seg = { ...s, dash, offset };
                offset += dash + 2;
                return seg;
              });
              return (
                <svg width={130} height={130} style={{ flexShrink: 0 }}>
                  <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--border, #e5e7eb)" strokeWidth={stroke} />
                  {segments.map((s) => (
                    <circle
                      key={s.name}
                      cx={cx} cy={cy} r={r}
                      fill="none"
                      stroke={s.color}
                      strokeWidth={stroke}
                      strokeDasharray={`${s.dash} ${circ - s.dash}`}
                      strokeDashoffset={-s.offset}
                      style={{ transform: "rotate(-90deg)", transformOrigin: `${cx}px ${cy}px` }}
                    />
                  ))}
                </svg>
              );
            })()}
            {/* Legend with site names */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
              {[
                { name: "Sky Hill",  pct: 50, color: "#4F8EF7" },
                { name: "Hill Side", pct: 20, color: "#FF6B3D" },
                { name: "Sasol",     pct: 30, color: "#F5C842" },
              ].map((s) => (
                <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 12, color: "var(--tc1, #222)" }}>{s.name}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{s.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div><br/>

      {/* Bottom Row */}
      <div className="g2">
        {/* Recent Orders — Customer replaced with Site */}
        <div className="panel">
          <div className="ph">
            <span className="ph-title">Recent Orders</span>
            <span className="ph-sub">Last 5 transactions</span>
          </div>
          <table className="tbl">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Site</th>
                <th>Date</th>
                <th>Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {MOCK.orders.slice(0, 5).map((o, i) => (
                <tr key={o.id}>
                  <td className="mono t-flame">{o.id}</td>
                  <td
                    style={{
                      fontSize: 11,
                      maxWidth: 130,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {sites[i % sites.length]}
                  </td>
                  <td className="fs11 tc2">{o.product}</td>
                  <td className="mono fw7">{fmt.currency(o.total)}</td>
                  <td>
                    <Badge status={o.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Collections per Site — cylinder type & qty */}
        <div className="panel">
          <div className="ph">
            <span className="ph-title">
              <i className="bi bi-arrow-repeat" /> Collections per Site
            </span>
            <span className="ph-sub">Cylinder type &amp; qty</span>
          </div>
          <div
            className="pb"
            style={{ display: "flex", flexDirection: "column", gap: 14 }}
          >
            {COLLECTIONS_PER_SITE.map((c) => (
              <div
                key={c.site}
                style={{
                  background: "var(--deep)",
                  borderRadius: 8,
                  padding: "12px 14px",
                  border: "1px solid var(--border)",
                }}
              >
                <div className="fb mb8">
                  <span className="fs12 fw7">{c.site}</span>
                  <span className="fs11 tc2 mono">
                    {c.cylinders.reduce((s, x) => s + x.qty, 0)} total
                  </span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {c.cylinders.map((cyl) => (
                    <div key={cyl.type} className="fb fs11">
                      <span className="tc2">{cyl.type}</span>
                      <span className="mono fw7">{cyl.qty} cyl</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Inventory Status — real cylinder types & site names */}
      <div className="panel" style={{ marginTop: 16 }}>
        <div className="ph">
          <span className="ph-title">
            <i className="bi bi-bar-chart-line-fill" /> Inventory Status Overview
          </span>
        </div>
        <div className="pb">
          <div className="g3">
            {INVENTORY.map((item) => {
              const pct = Math.round((item.stock / item.maxStock) * 100);
              const fillColor =
                item.status === "Critical"
                  ? "var(--danger)"
                  : item.status === "Low"
                    ? "var(--warn)"
                    : "var(--teal)";
              return (
                <div
                  key={item.id}
                  style={{
                    background: "var(--deep)",
                    borderRadius: 8,
                    padding: "12px 14px",
                    border: "1px solid var(--border)",
                  }}
                >
                  <div className="fb mb8">
                    <span className="fs12 fw6">{item.cylinder}</span>
                    <Badge status={item.status} />
                  </div>
                  <div className="fs11 tc2 mb8 mono">
                    {fmt.number(item.stock)} cylinders
                  </div>
                  <div className="prog-wrap">
                    <div
                      className="prog-fill"
                      style={{ width: `${pct}%`, background: fillColor }}
                    />
                  </div>
                  <div className="fs10 tc3" style={{ marginTop: 4 }}>
                    {pct}% capacity · {item.site}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;